// =============================================================================
// Gradient bars — visual summary for ferritin + hemoglobin.
//
// Each bar has fixed stage/severity segments (styled via CSS classes in
// styles.css). This module positions the value-marker drop on the correct
// segment and updates the label line beneath each bar.
//
// Positioning uses piecewise-linear interpolation across the scale anchor
// points shown under each bar: the bar width linearly fills between each
// labeled anchor (e.g., ferritin 0 → 15 → 25 → 30 → 50 → 100+).
//
// Bars are hidden when the user did not enter the marker.
// =============================================================================

import type { FerritinZone } from '../engine/thresholds.js';
import { classifyFerritin } from '../engine/thresholds.js';
import type { LabInputs, LifeStage } from '../engine/types.js';

interface ScaleAnchor {
  /** Value at this anchor point (e.g., 0, 15, 25...). */
  value: number;
  /** Position along the bar 0–100. */
  percent: number;
}

/** Ferritin scale — anchors match the segment widths in styles.css + HTML. */
const FERRITIN_SCALE: readonly ScaleAnchor[] = [
  { value: 0, percent: 0 },
  { value: 15, percent: 15 }, // stage_4 → stage_3 boundary
  { value: 25, percent: 25 }, // stage_3 → stage_2 boundary
  { value: 30, percent: 30 }, // stage_2 → stage_1 boundary
  { value: 50, percent: 50 }, // stage_1 → stage_0 boundary
  { value: 100, percent: 100 }, // replete scale cap
] as const;

/** Hemoglobin scale — anchors map 60–160 g/L across severity bands. */
const HEMOGLOBIN_SCALE: readonly ScaleAnchor[] = [
  { value: 60, percent: 0 },
  { value: 70, percent: 10 }, // severe → moderate boundary
  { value: 100, percent: 40 }, // moderate → mild boundary
  { value: 120, percent: 60 }, // mild → normal boundary
  { value: 160, percent: 100 }, // normal cap
] as const;

/**
 * Piecewise-linear interpolation of a value across the scale anchors.
 * Values below the first anchor clamp to 0; values above the last clamp to 100.
 */
export function interpolatePosition(value: number, scale: readonly ScaleAnchor[]): number {
  const first = scale[0]!;
  const last = scale[scale.length - 1]!;
  if (value <= first.value) return first.percent;
  if (value >= last.value) return last.percent;
  for (let i = 0; i < scale.length - 1; i += 1) {
    const a = scale[i]!;
    const b = scale[i + 1]!;
    if (value >= a.value && value <= b.value) {
      const ratio = (value - a.value) / (b.value - a.value);
      return a.percent + ratio * (b.percent - a.percent);
    }
  }
  return last.percent;
}

export function ferritinPosition(value: number): number {
  return interpolatePosition(value, FERRITIN_SCALE);
}

export function hemoglobinPosition(value: number): number {
  return interpolatePosition(value, HEMOGLOBIN_SCALE);
}

// -----------------------------------------------------------------------------
// Label copy — "22 μg/L · Stage 3"
// -----------------------------------------------------------------------------

export function ferritinLabel(value: number, lifeStage: LifeStage): string {
  const zone = classifyFerritin(value, lifeStage);
  return `<strong>${fmtNum(value)}</strong> μg/L · ${ferritinZoneLabel(zone)}`;
}

function ferritinZoneLabel(zone: FerritinZone): string {
  // Plain-language zone labels. Stage numbers stay for research continuity;
  // the descriptors after are written at an 8th-grade reading level and
  // avoid "replete" / "repletion" per FF voice rules.
  switch (zone) {
    case 'stage_0_replete':
      return 'Stage 0 · iron stores look healthy';
    case 'stage_1_incipient':
      return 'Stage 1 · iron stores starting to drop';
    case 'stage_2_depletion':
      return 'Stage 2 · iron reserves running low';
    case 'stage_3_ide':
      return 'Stage 3 · iron running short for red-cell production';
    case 'stage_4_ida_range':
      return 'Stage 4 range · iron shortage';
    case 'elevated_normal':
      return 'Above the healthy range';
    case 'overload_trigger':
      return 'Iron overload evaluation zone';
    case 'very_high':
      return 'Very high (> 1000 μg/L)';
  }
}

export function hemoglobinLabel(value: number, lifeStage: LifeStage): string {
  const descriptor = hemoglobinDescriptor(value, lifeStage);
  return `<strong>${fmtNum(value)}</strong> g/L · ${descriptor}`;
}

function hemoglobinDescriptor(value: number, lifeStage: LifeStage): string {
  // Use the life-stage anemia threshold to describe the zone.
  // Keeps descriptors short; full per-zone wording is in the table.
  if (value < 50) return 'transfusion territory';
  if (value < 70) return 'severe anemia';
  if (value < 80 && lifeStage === 'postpartum_within_6mo') return 'severe postpartum';
  if (value < 100) return 'moderate anemia';
  if (value < 120) return 'mild anemia';
  return 'within normal range';
}

function fmtNum(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return String(Math.round(n * 10) / 10);
}

// -----------------------------------------------------------------------------
// DOM rendering
// -----------------------------------------------------------------------------

/** Find a .gradient-row element by data-ff-bar key and update position + label. */
function renderBar(key: 'ferritin' | 'hemoglobin', percent: number, label: string): void {
  const row = document.querySelector<HTMLElement>(`[data-ff-bar="${key}"]`);
  if (!row) return;
  const marker = row.querySelector<HTMLElement>('.value-marker');
  if (marker) marker.style.left = `${percent}%`;
  const valueEl = row.querySelector<HTMLElement>('.gradient-value');
  if (valueEl) valueEl.innerHTML = label;
  row.hidden = false;
}

function hideBar(key: 'ferritin' | 'hemoglobin'): void {
  const row = document.querySelector<HTMLElement>(`[data-ff-bar="${key}"]`);
  if (row) row.hidden = true;
}

/**
 * Render both gradient bars from inputs. Shows only bars whose value was
 * provided. Caller passes the full LabInputs so life-stage context applies.
 *
 * Also rewrites the heading above the bars so the wording matches how many
 * bars are actually shown ("...your two most important values" / "...your
 * ferritin" / "...your hemoglobin"), and hides the whole visualization
 * block when neither value is present.
 */
export function renderGradientBars(inputs: LabInputs): void {
  const hasFerritin = inputs.ferritin !== undefined;
  const hasHb = inputs.hemoglobin !== undefined;

  if (hasFerritin) {
    renderBar(
      'ferritin',
      ferritinPosition(inputs.ferritin!),
      ferritinLabel(inputs.ferritin!, inputs.lifeStage),
    );
  } else {
    hideBar('ferritin');
  }

  if (hasHb) {
    renderBar(
      'hemoglobin',
      hemoglobinPosition(inputs.hemoglobin!),
      hemoglobinLabel(inputs.hemoglobin!, inputs.lifeStage),
    );
  } else {
    hideBar('hemoglobin');
  }

  updateVizHeading(hasFerritin, hasHb);
}

/**
 * Toggle the visualization container + rewrite the heading to match how
 * many bars are currently visible. Two-bar wording was the original
 * "...your two most important values"; one-bar variants name the value;
 * zero-bar hides the block entirely.
 */
function updateVizHeading(hasFerritin: boolean, hasHb: boolean): void {
  const container = document.querySelector<HTMLElement>(
    '[data-ff-bind="vizContainer"], .threshold-visualization',
  );
  const heading = document.querySelector<HTMLElement>('.viz-eyebrow');
  if (!container || !heading) return;

  if (!hasFerritin && !hasHb) {
    container.hidden = true;
    return;
  }
  container.hidden = false;

  if (hasFerritin && hasHb) {
    heading.textContent = 'Visual summary — where your two most important values fall';
  } else if (hasFerritin) {
    heading.textContent = 'Visual summary — where your ferritin falls';
  } else {
    heading.textContent = 'Visual summary — where your hemoglobin falls';
  }
}
